"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAnchoredPosition } from "@/lib/hooks/useAnchoredPosition";
import type { CustomEmoji } from "@/lib/api/emojis";
import type { StandardEmoji } from "@/lib/standard-emojis";
import { Emoji } from "@/components/ui/Emoji";

export type EmojiSuggestion =
  | { kind: "standard"; emoji: StandardEmoji }
  | { kind: "custom"; emoji: CustomEmoji };

/** One suggestion row; six of them is a comfortable list. */
const ROW_HEIGHT = 34;
const MAX_ROWS = 8;

export function EmojiSuggestionDropdown({
  suggestions,
  selectedIndex,
  onSelect,
  anchorRef,
}: {
  suggestions: EmojiSuggestion[];
  selectedIndex: number;
  onSelect: (suggestion: EmojiSuggestion) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Above the input by preference, so the caret you're typing at stays visible.
  const pos = useAnchoredPosition(anchorRef, suggestions.length > 0, {
    preferred: "above",
    desiredHeight: Math.min(suggestions.length, MAX_ROWS) * ROW_HEIGHT,
    maxWidth: 320,
    revision: suggestions,
  });

  useEffect(() => {
    const container = dropdownRef.current;
    if (!container) return;
    const item = container.querySelector(`[data-emoji-index="${selectedIndex}"]`) as HTMLElement | null;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (suggestions.length === 0 || !pos) return null;

  // Find the index where custom suggestions start (for the divider label)
  const firstCustomIdx = suggestions.findIndex((s) => s.kind === "custom");

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-surface rounded-lg border border-border shadow-lg z-[9999] overflow-y-auto overscroll-contain"
      style={{
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
      }}
    >
      {suggestions.map((s, i) => {
        const showCustomHeader = i === firstCustomIdx && firstCustomIdx > 0;
        return (
          <div key={`${s.kind}-${s.kind === "custom" ? s.emoji.id : s.emoji.shortcode}`}>
            {showCustomHeader && (
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-subtle-foreground bg-surface-2 sticky top-0">
                Gruppens emojis
              </div>
            )}
            <button
              data-emoji-index={i}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(s);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                i === selectedIndex ? "bg-primary-muted" : "hover:bg-surface-2"
              }`}
            >
              {/* One renderer for both kinds — these were a 20px glyph beside a
                  24px image, in rows meant to line up with each other. The
                  glyph is hidden from assistive tech: the shortcode beside it
                  is the row's name, and naming it twice reads twice. */}
              <span className="w-6 text-center shrink-0">
                <Emoji
                  emoji={s.kind === "standard" ? s.emoji.char : s.emoji.shortcode}
                  url={s.kind === "standard" ? null : s.emoji.url}
                  size="lg"
                  loading="lazy"
                  label=""
                />
              </span>
              <span className="text-sm text-foreground truncate">
                :{s.emoji.shortcode}:
              </span>
            </button>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
